import type {
  ContactAttachment,
  ContactConversation,
  ContactMessage,
} from '../../app/legacy-types';

export type ContactCenterProvider = 'ALL' | 'TELEGRAM' | 'MAX' | 'EMAIL';
export type ContactCenterStatusFilter = 'ACTIVE' | 'OPEN' | 'WAITING' | 'RESOLVED' | 'CLOSED';

export type ContactCenterFilters = {
  status?: ContactCenterStatusFilter;
  provider?: ContactCenterProvider;
  mine?: boolean;
  unread?: boolean;
  unanswered?: boolean;
  archived?: boolean;
  spam?: boolean;
  search?: string;
};

export const DEFAULT_CONTACT_CENTER_FILTERS: Readonly<ContactCenterFilters> = Object.freeze({
  status: 'ACTIVE',
  provider: 'ALL',
  search: '',
});

export function filterConversations(
  rows: ContactConversation[],
  filters: Pick<ContactCenterFilters, 'provider' | 'search'>,
): ContactConversation[] {
  const provider = String(filters.provider || 'ALL').toUpperCase();
  const needle = String(filters.search || '').trim().toLocaleLowerCase('ru-RU');
  return rows.filter(item => {
    if (provider !== 'ALL' && item.provider.toUpperCase() !== provider) return false;
    if (!needle) return true;
    return [
      item.title,
      item.customer_name,
      item.customer_username,
      item.customer_phone,
      item.customer_external_id,
      item.last_message_text,
      item.channel_name,
      item.linked_company_name,
      item.linked_contact_name,
      item.linked_ticket_display,
    ].filter(Boolean).some(value => String(value).toLocaleLowerCase('ru-RU').includes(needle));
  });
}

export function pickConversation(rows: ContactConversation[], selectedId?: string | null): ContactConversation | null {
  return rows.find(item => item.id === selectedId) ?? rows[0] ?? null;
}

export function channelLabel(value?: string | null): string {
  const normalized = String(value || '').trim().toUpperCase();
  if (normalized.includes('TELEGRAM')) return 'Telegram';
  if (normalized.includes('MAX')) return 'MAX';
  if (normalized === 'EMAIL' || normalized.includes('MAIL')) return 'Email';
  return String(value || 'Портал');
}

export function conversationStatusLabel(status: ContactConversation['status']): string {
  return ({ OPEN: 'Открыт', WAITING: 'Ожидание', RESOLVED: 'Решён', CLOSED: 'В архиве' } as const)[status] ?? status;
}

export function formatContactDateTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function formatContactShort(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export function meaningfulConversationDescription(messages: ContactMessage[], fallback: string): string {
  const candidates = messages
    .filter(item => item.direction === 'INBOUND')
    .map(item => String(item.body || '').trim())
    .filter(value => value && !/^\/(start|help|menu)\b/i.test(value))
    .filter(value => !/^(?:📎|🖼️|🎬|🎧|🎤)\s*(?:Файл|Файлы|Изображение|Видео|Аудио|Голосовое)/i.test(value))
    .filter(value => value.length >= 8);
  return (candidates.at(-1) || fallback || 'Обращение из контакт-центра').slice(0, 4000);
}

export function friendlyDeliveryError(value?: string | null): string {
  const text = String(value || '').trim();
  const normalized = text.toLowerCase();
  if (!text) return 'Не удалось отправить вложение. Повторите попытку.';
  if (normalized.includes('не вернул токен') || normalized.includes('данные вложения')) return 'MAX не вернул данные вложения. Файл оставлен для повторной отправки.';
  if (normalized.includes('attachment.not.ready') || normalized.includes('ещё обрабатывает')) return 'MAX ещё обрабатывает вложение. Повторите отправку через несколько секунд.';
  if (normalized.includes('extension') || normalized.includes('формат файла')) return 'MAX не поддерживает этот формат. Отправьте файл в ZIP или выберите другой формат.';
  if (normalized.includes('401') || normalized.includes('403') || normalized.includes('авториза')) return 'Канал отклонил авторизацию. Проверьте подключение.';
  if (normalized.includes('оставлен в форме') || normalized.includes('повторите')) return text;
  return 'Не удалось отправить сообщение. Текст и файлы оставлены для повторной отправки.';
}

export function deliveryLabel(message: ContactMessage): string {
  const delivery = message.payload?.delivery as {
    textDelivered?: boolean;
    deliveredFileIndexes?: number[];
    failedFiles?: Array<{ name?: string; error?: string }>;
  } | undefined;
  const failed = Array.isArray(delivery?.failedFiles) ? delivery.failedFiles : [];
  const textFailed = Boolean(message.payload?.delivery) && delivery?.textDelivered === false;
  if (!failed.length && !textFailed) {
    if (message.provider_status === 'DELETED' || message.deleted_for_everyone_at) return 'Удалено у всех';
    if (message.provider_status === 'FAILED') return 'Ошибка отправки';
    if (message.provider_status === 'EDITED' || message.edited_at) return 'Отправлено · изменено';
    if (message.provider_status === 'READ') return 'Прочитано';
    if (message.provider_status === 'DELIVERED') return 'Доставлено';
    return message.direction === 'OUTBOUND' ? 'Отправлено' : 'Получено';
  }
  const delivered = Array.isArray(delivery?.deliveredFileIndexes) ? delivery.deliveredFileIndexes.length : 0;
  return `${textFailed ? 'Текст не отправлен · ' : ''}Отправлено файлов: ${delivered} · Не отправлено: ${failed.length}`;
}

export function isEmojiOnly(text: string): boolean {
  const value = text.trim();
  return Boolean(value) && /^(?:[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\uFE0F\u200D\s]){1,8}$/u.test(value);
}

export function contactAttachments(message: ContactMessage): ContactAttachment[] {
  const payload = message.payload as { _portalAttachments?: ContactAttachment[] } | undefined;
  const rows = Array.isArray(payload?._portalAttachments) ? payload._portalAttachments : [];
  const seen = new Set<string>();
  return rows.map((item, sourceIndex) => ({ ...item, sourceIndex })).filter(item => {
    const key = item.storageFileId
      || item.fileId
      || item.url?.replace(/[?#].*$/, '')
      || item.thumbnailUrl?.replace(/[?#].*$/, '')
      || `${item.name?.toLowerCase()}:${item.size ?? ''}:${item.mimeType ?? item.type}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function attachmentUrl(messageId: string, index: number, download = false): string {
  return `/api/v1/contact-center/messages/${messageId}/attachments/${index}${download ? '?download=1' : ''}`;
}

export function isImageAttachment(item: ContactAttachment): boolean {
  return item.type === 'image' || String(item.mimeType || '').toLowerCase().startsWith('image/');
}

export function isVideoAttachment(item: ContactAttachment): boolean {
  const mime = String(item.mimeType || '').toLowerCase();
  return item.type === 'video' || item.type === 'animation' || mime.startsWith('video/');
}

export function isAudioAttachment(item: ContactAttachment): boolean {
  return Boolean(item.isVoice) || item.type === 'voice' || item.type === 'audio' || String(item.mimeType || '').startsWith('audio/');
}

export type ContactMessageRow =
  | { kind: 'date'; key: string; label: string }
  | { kind: 'message'; key: string; item: ContactMessage; grouped: boolean; showHeader: boolean };

export function groupContactMessages(messages: ContactMessage[]): ContactMessageRow[] {
  const rows: ContactMessageRow[] = [];
  let previousDate = '';
  let previousDirection = '';
  let previousSender = '';
  messages.forEach((item, index) => {
    const dateLabel = new Date(item.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
    if (dateLabel !== previousDate) {
      rows.push({ kind: 'date', key: `date:${dateLabel}`, label: dateLabel });
      previousDate = dateLabel;
      previousDirection = '';
      previousSender = '';
    }
    const sender = item.sender_name || (item.direction === 'OUTBOUND' ? 'Оператор' : item.direction === 'SYSTEM' ? 'Система' : 'Клиент');
    const previous = index > 0 ? messages[index - 1] : null;
    const closeEnough = previous ? Math.abs(new Date(item.created_at).getTime() - new Date(previous.created_at).getTime()) < 5 * 60_000 : false;
    const grouped = previousDirection === item.direction && previousSender === sender && closeEnough;
    rows.push({ kind: 'message', key: item.id, item, grouped, showHeader: !grouped || item.direction === 'SYSTEM' });
    previousDirection = item.direction;
    previousSender = sender;
  });
  return rows;
}
